/* eslint-disable no-comments/disallowComments */
import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';

type TitleSize = 'h1' | 'h2' | 'h3';
type TitleTone = 'primary' | 'accent' | 'overlay' | 'inherit';

interface TitleProps {
    children: React.ReactNode;
    /** Heading level — controls both the HTML element and the visual size. */
    size?: TitleSize;
    /** Color tone: `primary` (darkNavy, default), `accent` (turquoise), or `inherit` (inherits color from parent). */
    tone?: TitleTone;
    className?: string;
}

const TONE_COLOR: Record<TitleTone, string> = {
    primary: Colors.darkNavy,
    accent: Colors.turquoise,
    overlay: Colors.white,
    inherit: 'inherit',
};

const baseStyles = {
    fontWeight: 700,
    margin: 0,
};

const shouldForwardTitleProp = (prop: string) => prop !== '$tone';

type TitleStyleProps = { $tone: TitleTone };

const toneStyle = ({ $tone }: TitleStyleProps) => ({ color: TONE_COLOR[$tone] });

const H1 = styled('h1', { shouldForwardProp: (p) => shouldForwardTitleProp(p as string) })<TitleStyleProps>(
    baseStyles,
    { fontSize: 28 },
    toneStyle,
);
const H2 = styled('h2', { shouldForwardProp: (p) => shouldForwardTitleProp(p as string) })<TitleStyleProps>(
    baseStyles,
    { fontSize: 18 },
    toneStyle,
);
const H3 = styled('h3', { shouldForwardProp: (p) => shouldForwardTitleProp(p as string) })<TitleStyleProps>(
    baseStyles,
    { fontSize: 15, fontWeight: 500 },
    toneStyle,
);

const COMPONENTS: Record<TitleSize, React.ElementType> = { h1: H1, h2: H2, h3: H3 };

export function Title({ children, size = 'h2', tone = 'primary', className }: TitleProps) {
    const Component = COMPONENTS[size];
    return (
        <Component $tone={tone} className={className}>
            {children}
        </Component>
    );
}
/* eslint-enable no-comments/disallowComments */
